import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { PageLoading } from '@pv/common/components';
import { lazyImport } from '@pv/common/utils';

// Eager: the search landing is the primary SEO entry point, and the route-group
// layouts wrap many routes, so keeping them in the main bundle avoids an extra
// round trip on first paint. Every other page is split into its own chunk and
// loaded on navigation. Pages are named exports, so each dynamic import is
// adapted to the default export that React.lazy expects.
import { SearchPageLayout } from './pages/Search/components/SearchPageLayout';
import { SearchResultsPage } from './pages/Search';
import { MarketplaceLayout } from './components/MarketplaceLayout';
import { GuestAuthLayout } from './components/Auth/GuestAuthLayout';
import { GuestLayout } from './components/GuestLayout';
import { InquiryLayout } from './pages/ExpressBook/InquiryLayout';

const OrganizationSpacesPage = lazyImport(() =>
  import('./pages/Organizations/OrganizationSpacesPage').then((m) => ({
    default: m.OrganizationSpacesPage,
  }))
);
const VenueSpacesPage = lazyImport(() =>
  import('./pages/Venues/VenueSpacesPage').then((m) => ({
    default: m.VenueSpacesPage,
  }))
);
const VenueBookingsPage = lazyImport(() =>
  import('./pages/ExpressBook/Step1/VenueBookingsPage').then((m) => ({
    default: m.VenueBookingsPage,
  }))
);
const SelectMenuPage = lazyImport(() =>
  import('./pages/ExpressBook/Step2/SelectMenuPage').then((m) => ({
    default: m.SelectMenuPage,
  }))
);
const SpacePage = lazyImport(() =>
  import('./pages/Venues/Spaces/SpacePage').then((m) => ({
    default: m.SpacePage,
  }))
);
const MarketplaceEventSuccessPage = lazyImport(() =>
  import('./pages/ExpressBook/Step1/MarketplaceEventSuccessPage').then((m) => ({
    default: m.MarketplaceEventSuccessPage,
  }))
);
const MarketplaceEventErrorPage = lazyImport(() =>
  import('./pages/ExpressBook/Step1/MarketplaceEventErrorPage').then((m) => ({
    default: m.MarketplaceEventErrorPage,
  }))
);
const ExpressBookCheckoutPage = lazyImport(() =>
  import('./pages/ExpressBook/Step3/ExpressBookCheckoutPage').then((m) => ({
    default: m.ExpressBookCheckoutPage,
  }))
);
const ExpressBookConfirmationPage = lazyImport(() =>
  import('./pages/ExpressBook/Step4/ExpressBookConfirmationPage').then((m) => ({
    default: m.ExpressBookConfirmationPage,
  }))
);
const NotFoundPage = lazyImport(() =>
  import('./pages/NotFound/NotFoundPage').then((m) => ({
    default: m.NotFoundPage,
  }))
);
const ViewOnlyMenu = lazyImport(() =>
  import('./pages/Menu/ViewOnlyMenu').then((m) => ({ default: m.ViewOnlyMenu }))
);
const GuestResetPasswordPage = lazyImport(() =>
  import('./pages/Auth/GuestResetPasswordPage').then((m) => ({
    default: m.GuestResetPasswordPage,
  }))
);
const GuestConfirmEmailPage = lazyImport(() =>
  import('./pages/Auth/GuestConfirmEmailPage').then((m) => ({
    default: m.GuestConfirmEmailPage,
  }))
);
const MagicLinkPage = lazyImport(() =>
  import('./pages/Auth/MagicLinkPage').then((m) => ({
    default: m.MagicLinkPage,
  }))
);
const GuestProfilePage = lazyImport(() =>
  import('./pages/Auth/GuestProfilePage').then((m) => ({
    default: m.GuestProfilePage,
  }))
);
const EventPlansPage = lazyImport(() =>
  import('./pages/EventPlans/EventPlansPage').then((m) => ({
    default: m.EventPlansPage,
  }))
);
const FavoritesPage = lazyImport(() =>
  import('./pages/Favorites/FavoritesPage').then((m) => ({
    default: m.FavoritesPage,
  }))
);
const ReviewPage = lazyImport(() =>
  import('./pages/Events/Review').then((m) => ({ default: m.ReviewPage }))
);
const PendingReviewsPage = lazyImport(() =>
  import('./pages/Reviews/PendingReviewsPage').then((m) => ({
    default: m.PendingReviewsPage,
  }))
);

export const AppRoutes = () => (
  <Suspense fallback={<PageLoading />}>
    <Routes>
      <Route element={<GuestAuthLayout />}>
        <Route
          path="/guests/sign_in"
          element={<Navigate to="/?guest_auth=login" replace />}
        />
        <Route
          path="/guests/sign_up"
          element={<Navigate to="/?guest_auth=signup" replace />}
        />
        <Route
          path="/guests/password/new"
          element={<GuestResetPasswordPage />}
        />
        <Route
          path="/guests/password/edit"
          element={<GuestResetPasswordPage />}
        />
        <Route
          path="/guests/confirmation"
          element={<GuestConfirmEmailPage />}
        />
        <Route path="/guests/magic_link" element={<MagicLinkPage />} />
      </Route>

      <Route element={<SearchPageLayout />}>
        <Route path="/" element={<SearchResultsPage />} />
        <Route path="/cities/:city" element={<SearchResultsPage />} />
        <Route path="/favorites" element={<FavoritesPage />} />
      </Route>
      <Route element={<GuestLayout />}>
        <Route path="/guests/profile" element={<GuestProfilePage />} />
        <Route path="/guests/event-plans/" element={<EventPlansPage />} />
        <Route
          path="/guests/events/:eventSlug/review"
          element={<ReviewPage />}
        />
        <Route path="/guests/reviews" element={<PendingReviewsPage />} />
      </Route>
      <Route element={<MarketplaceLayout />}>
        <Route
          path="/organizations/:slug"
          element={<OrganizationSpacesPage />}
        />
        <Route path="/venues/:venueSlug" element={<VenueSpacesPage />} />
        <Route path="/venues/:venueSlug/menu" element={<ViewOnlyMenu />} />
        <Route
          path="/venues/:venueSlug/spaces/:spaceSlug"
          element={<SpacePage />}
        />
        <Route path="not-found" element={<NotFoundPage />} />
      </Route>
      <Route element={<InquiryLayout />}>
        <Route
          path="/venues/:venueSlug/spaces/:spaceSlug/bookings/new"
          element={<VenueBookingsPage />}
        />
        <Route
          path="/venues/:venueSlug/spaces/:spaceSlug/bookings/:eventSlug/request-sent/"
          element={<MarketplaceEventSuccessPage />}
        />
        <Route
          path="/venues/:venueSlug/spaces/:spaceSlug/bookings/error"
          element={<MarketplaceEventErrorPage />}
        />
      </Route>
      <Route
        path="/venues/:venueSlug/spaces/:spaceSlug/bookings/:eventSlug"
        element={<SelectMenuPage />}
      />
      <Route
        path="/venues/:venueSlug/spaces/:spaceSlug/bookings/:eventSlug/checkout"
        element={<ExpressBookCheckoutPage />}
      />

      <Route
        path="/venues/:venueSlug/spaces/:spaceSlug/bookings/:eventSlug/success"
        element={<ExpressBookConfirmationPage />}
      />

      <Route path="/*" element={<Navigate to="/" />} />
    </Routes>
  </Suspense>
);
